import React from 'react';

import type { CardId } from '@vlabs/api/artemy/model';
import { CrossIcon } from '@vlabs/icons';
import { AttachCardsToListButtonPoint3 } from '@vlabs/monokit/features/attach-cards-to-list';
import { DeleteCardsButtonPoint3 } from '@vlabs/monokit/features/delete-cards';
import { DetachCardsFromListButtonPoint3 } from '@vlabs/monokit/features/detach-cards-from-list';
import { MoveCardsToListButtonPoint3 } from '@vlabs/monokit/features/move-cards-to-list';
import { useHasPermission } from '@vlabs/permissions';
import type { BulkActionsProps } from '@vlabs/uikit';
import { BulkActions } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';
import { useTranslation } from 'react-i18next';

import st from './CardsBulkActions.module.sass';
import { useListIdFromLocation } from '../../../useListIdFromLocation';

export interface CardsBulkActionsProps extends BulkActionsProps {
  selectedCardIds: CardId[];
  onCancel: VoidFunction;
}

function CardsBulkActions({
  selectedCardIds,
  onCancel,
  onCheckboxClick,
  indeterminate = false,
}: CardsBulkActionsProps): React.JSX.Element {
  const { t } = useTranslation();
  const listId = useListIdFromLocation();
  const canUpdateCard = useHasPermission('card', 'update');
  const canDeleteCard = useHasPermission('card', 'delete');

  return (
    <BulkActions
      data-testid="CardsBulkActions"
      indeterminate={indeterminate}
      onCheckboxClick={onCheckboxClick}
    >
      <div className={st.Root}>
        <span
          className={st.SelectedCardsCount}
          data-testid="CardsBulkActions-SelectedCount"
        >
          {t('list-details-page:cards_bulk_actions.selected_count', { count: (selectedCardIds?.length ?? 0) })}
        </span>
        <div className={st.Controls}>
          {!!canUpdateCard && (
            <AttachCardsToListButtonPoint3
              cardIds={selectedCardIds}
              size="small"
              variant="flat-inverted"
            />
          )}
          {!!canUpdateCard && (
            <MoveCardsToListButtonPoint3
              cardIds={selectedCardIds}
              fromListId={listId}
              size="small"
              variant="flat-inverted"
            />
          )}
          {!!canUpdateCard && (
            <DetachCardsFromListButtonPoint3
              cardIds={selectedCardIds}
              fromListId={listId}
              size="small"
              variant="flat-inverted"
            />
          )}
          {!!canDeleteCard && (
            <DeleteCardsButtonPoint3
              cardIds={selectedCardIds}
              size="small"
              variant="flat-inverted"
            />
          )}
          <IconButton
            data-testid="CardsBulkActions-CancelBtn"
            icon={<CrossIcon />}
            kind="secondary"
            onClick={onCancel}
            shape="square"
            size="small"
            variant="flat-inverted"
          />
        </div>
      </div>
    </BulkActions>
  );
}

export { CardsBulkActions };
